import React, { useState } from "react";
import { Check, Zap, Archive } from "lucide-react";
import { FiPackage } from 'react-icons/fi';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { getReleaseStatusColor } from "@/lib/colorsMap";
import { Skeleton } from "@/components/ui/skeleton";

export function ReleaseDropdown({ 
  task, 
  onTaskChange,
  onRefreshActivities,
  className,
  triggerClassName,
  contentClassName,
  variant = "default", // "default" | "icon-only" | "minimal"
  size = "sm", // "sm" | "xs"
  showLabel = true,
  showReleaseDate = true,
  maxLabelWidth = "180px",
  isLoading = false
}) {
  const [open, setOpen] = useState(false);
  const [showMoveToBacklogDialog, setShowMoveToBacklogDialog] = useState(false);
  const [pendingRelease, setPendingRelease] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null); // 'keep' or 'move'
  const { axiosInstance } = useAuth();
  const { showLoading, hideLoading } = useGlobalLoading();
  const { toast } = useToast();

  const availableReleases = task?.availableReleases || [];

  // Separate releases into active and inactive (backlog)
  const activeReleases = [];
  const backlogReleases = [];
  
  availableReleases.forEach(release => {
    // A release is active if it has a start date in the past or today
    const isActive = release.startDate && new Date(release.startDate) <= new Date();
    if (isActive) {
      activeReleases.push(release);
    } else {
      backlogReleases.push(release);
    }
  });

  const handleReleaseChange = async (release) => {
    // Get organization slug from task
    const organizationSlug = task.organization?.slug || task.organizationSlug;
    
    if (!task.key || !organizationSlug) {
      console.error("Missing task key or organization slug");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Unable to update task - missing required information",
      });
      return;
    }

    if (release.id === "active") {
      // Move to active week (no release)
      // 1. Capture old values for rollback
      const oldRelease = task.release;
      const oldScope = task.scope;
      
      // 2. Optimistically update UI immediately
      const optimisticTask = {
        ...task,
        release: null,
        scope: 'tracked'
      };
      onTaskChange(optimisticTask);
      setOpen(false);
      
      // 3. Fire API calls (don't await before UI update)
      try {
        showLoading();
        // First remove the release if task has one
        if (oldRelease) {
          await axiosInstance.delete(`/tasks/${organizationSlug}/${task.key}/release`);
        }
        
        // Then update the scope
        await axiosInstance.patch(`/tasks/${organizationSlug}/${task.key}`, {
          scope: 'tracked'
        });

        // 4. Refresh just activities (new activity was created)
        if (onRefreshActivities) {
          await onRefreshActivities();
        }
      } catch (error) {
        // 5. Rollback on error
        console.error("Error updating task scope:", error);
        onTaskChange({ ...task, release: oldRelease, scope: oldScope });
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to move task to active week",
        });
      } finally {
        hideLoading();
      }
    } else if (release.id === "backlog") {
      // Move to backlog (no release)
      // 1. Capture old values for rollback
      const oldRelease = task.release;
      const oldScope = task.scope;
      
      // 2. Optimistically update UI immediately
      const optimisticTask = {
        ...task,
        release: null,
        scope: 'backlog'
      };
      onTaskChange(optimisticTask);
      setOpen(false);
      
      // 3. Fire API calls (don't await before UI update)
      try {
        showLoading();
        // First remove the release if task has one
        if (oldRelease) {
          await axiosInstance.delete(`/tasks/${organizationSlug}/${task.key}/release`);
        }
        
        // Then update the scope
        await axiosInstance.patch(`/tasks/${organizationSlug}/${task.key}`, {
          scope: 'backlog'
        });

        // 4. Refresh just activities (new activity was created)
        if (onRefreshActivities) {
          await onRefreshActivities();
        }
      } catch (error) {
        // 5. Rollback on error
        console.error("Error updating task scope:", error);
        onTaskChange({ ...task, release: oldRelease, scope: oldScope });
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to move task to backlog",
        });
      } finally {
        hideLoading();
      }
    } else {
      // Move to a specific release
      // Check if release is inactive and task is in active week
      const isReleaseInactive = !release.startDate || new Date(release.startDate) > new Date();
      const isTaskInActiveWeek = task.scope === 'tracked' || !task.scope;
      
      // Check if clicking on already selected release
      const isAlreadySelected = task.release?.id === release.id;
      
      if (isReleaseInactive && isTaskInActiveWeek && isAlreadySelected) {
        // If clicking on already selected inactive release while in active week,
        // show dialog to move to backlog
        setPendingRelease(release);
        setShowMoveToBacklogDialog(true);
        setOpen(false);
        return;
      }
      
      if (isReleaseInactive && isTaskInActiveWeek && !isAlreadySelected) {
        // If assigning new inactive release while in active week, show dialog
        setPendingRelease(release);
        setShowMoveToBacklogDialog(true);
        setOpen(false);
        return;
      }
      
      // Otherwise proceed normally
      await assignReleaseToTask(release, false);
    }
  };

  const assignReleaseToTask = async (release, alsoMoveToBacklog, actionType) => {
    const organizationSlug = task.organization?.slug || task.organizationSlug;
    
    // 1. Capture old values for rollback
    const oldRelease = task.release;
    const oldScope = task.scope;
    
    // 2. Optimistically update UI immediately
    const optimisticTask = {
      ...task,
      release: {
        id: release.id,
        name: release.name,
        status: release.status,
        statusDetails: release.statusDetails,
        startDate: release.startDate,
        targetDate: release.targetDate,
        actualReleaseDate: release.actualReleaseDate
      },
      scope: alsoMoveToBacklog ? 'backlog' : task.scope
    };
    onTaskChange(optimisticTask);
    setOpen(false);
    setLoadingAction(actionType);
    
    // 3. Fire API calls (don't await before UI update)
    try {
      showLoading();
      // Assign the release
      await axiosInstance.post(`/tasks/${organizationSlug}/${task.key}/release`, {
        release: { _id: release.id },
        isNewRelease: false
      });

      // If we also need to move to backlog scope
      if (alsoMoveToBacklog) {
        await axiosInstance.patch(`/tasks/${organizationSlug}/${task.key}`, {
          scope: 'backlog'
        });
      }

      // 4. Refresh just activities (new activity was created)
      if (onRefreshActivities) {
        await onRefreshActivities();
      }
    } catch (error) {
      // 5. Rollback on error
      console.error("Error updating task release:", error);
      onTaskChange({ ...task, release: oldRelease, scope: oldScope });
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update release",
      });
    } finally {
      hideLoading();
      setLoadingAction(null);
    }
  };

  const handleKeepInActiveWeek = async () => {
    if (pendingRelease) {
      await assignReleaseToTask(pendingRelease, false, 'keep');
    }
    setShowMoveToBacklogDialog(false);
    setPendingRelease(null);
  };

  const handleMoveToBacklog = async () => {
    if (pendingRelease) {
      await assignReleaseToTask(pendingRelease, true, 'move');
    }
    setShowMoveToBacklogDialog(false);
    setPendingRelease(null);
  };

  const renderTrigger = () => {
    if (isLoading) {
      // Skeleton for minimal variant (used in TaskDetails)
      if (variant === "minimal") {
        return (
          <Skeleton className="h-7 w-32 rounded-md bg-muted/60 border border-muted/30 shadow-sm" />
        );
      }
      // Skeleton for icon-only variant
      if (variant === "icon-only") {
        return (
          <Skeleton className={cn(
            "rounded-md bg-muted/60 border border-muted/30 shadow-sm",
            size === "xs" ? "h-3.5 w-3.5" : "h-4.5 w-4.5"
          )} />
        );
      }
      // Skeleton for default variant
      return (
        <Skeleton className="h-7 w-24 rounded-md bg-muted/60 border border-muted/30 shadow-sm" />
      );
    }

    if (variant === "icon-only") {
      return (
        <div 
          className={cn(
            "cursor-pointer flex items-center justify-center hover:opacity-60",
            triggerClassName
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {task.release ? (
            <FiPackage className={cn(
              size === "xs" ? "w-3.5 h-3.5" : "w-4.5 h-4.5",
              getReleaseStatusColor(task.release.status).icon
            )} />
          ) : task.scope === 'tracked' ? (
            <Zap className={cn(
              size === "xs" ? "w-3.5 h-3.5" : "w-4.5 h-4.5",
              "text-neutral-500"
            )} />
          ) : (
            <Archive className={cn(
              size === "xs" ? "w-3.5 h-3.5" : "w-4.5 h-4.5",
              "text-neutral-500"
            )} />
          )}
        </div>
      );
    }

    if (variant === "minimal") {
      return (
        <Button 
          variant="ghost" 
          size="sm" 
          className={cn(
            "h-7 px-2 bg-muted/60 hover:bg-muted/80 text-xs font-medium shadow-sm border border-muted/30",
            triggerClassName
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {task.release ? (
            <>
              <FiPackage className={cn("w-3 h-3 mr-1.5", getReleaseStatusColor(task.release.status).icon)} />
              <span className="text-primary truncate" style={{ maxWidth: maxLabelWidth }}>
                {task.release.name}
                {showReleaseDate && task.release.targetDate && (
                  <span className="text-primary/70 text-[10px]"> • {format(new Date(task.release.targetDate), 'MMM d')}</span>
                )}
              </span>
            </>
          ) : task.scope === 'tracked' ? (
            <>
              <Zap className="w-3 h-3 text-emerald-600 mr-1.5" />
              <span className="text-primary">
                {task.currentWeek ? `Week ${task.currentWeek.weekNumber}` : 'Active'}
                <span className="text-primary/70 text-[10px]"> • No Release</span>
              </span>
            </>
          ) : task.scope === 'backlog' ? (
            <>
              <Archive className="w-3 h-3 text-neutral-500 mr-1.5" />
              <span className="text-primary">
                Backlog
                <span className="text-primary/70 text-[10px]"> • No Release</span>
              </span>
            </>
          ) : (
            <>
              <FiPackage className="w-3 h-3 text-muted-foreground mr-1.5" />
              <span className="text-primary">Set scope</span>
            </>
          )}
        </Button>
      );
    }

    // Default variant
    return (
      <Button 
        variant="ghost" 
        size="sm" 
        className={cn(
          "h-7 px-2 flex items-center gap-1.5 justify-start text-left max-w-full",
          !task.release && task.scope !== 'tracked' && task.scope !== 'backlog' && "text-muted-foreground",
          triggerClassName
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {task.release && (
          <FiPackage className={cn(
            "w-5 h-5 flex-shrink-0",
            getReleaseStatusColor(task.release.status).icon
          )} />
        )}
        {showLabel && (
          <span className="truncate">
            {task.release ? task.release.name : "Set release"}
          </span>
        )}
      </Button>
    );
  };

  // If loading, just return the skeleton without the dropdown
  if (isLoading) {
    return (
      <div className={className}>
        {renderTrigger()}
      </div>
    );
  }

  return (
    <div className={className}>
      <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          {renderTrigger()}
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="start" 
          className={cn("w-auto min-w-[200px] max-w-[320px] mb-2", contentClassName)} 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Active Releases Section */}
          {activeReleases.length > 0 && (
            <>
              <DropdownMenuItem
                onClick={() => handleReleaseChange({ id: "active" })}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <Zap className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span>Active Week</span>
                <span className="text-xs text-muted-foreground">(No Release)</span>
                {task.scope === 'tracked' && !task.release && (
                  <Check className="ml-auto h-4 w-4" />
                )}
              </DropdownMenuItem>
              
              <div className="pl-4 border-l-2 border-emerald-200 dark:border-emerald-800 ml-4 my-1 space-y-0.5 max-h-[150px] overflow-y-auto">
                {activeReleases.map((release) => {
                  const colors = getReleaseStatusColor(release.status);
                  return (
                    <DropdownMenuItem
                      key={release.id}
                      onClick={() => handleReleaseChange(release)}
                      className="flex items-center gap-2 text-sm whitespace-nowrap pr-2"
                    >
                      <FiPackage className={cn("w-3.5 h-3.5 flex-shrink-0", colors.icon)} />
                      <span className="truncate">{release.name}</span>
                      {showReleaseDate && release.targetDate && (
                        <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                          {format(new Date(release.targetDate), 'MMM d')}
                        </span>
                      )}
                      {task.release?.id === release.id && (
                        <Check className="ml-auto h-4 w-4" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </div>
            </>
          )}
          
          {activeReleases.length > 0 && backlogReleases.length > 0 && (
            <DropdownMenuSeparator className="my-2" />
          )}
          
          {/* Backlog Section */}
          <>
            <DropdownMenuItem
              onClick={() => handleReleaseChange({ id: "backlog" })}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <Archive className="w-4 h-4 text-neutral-600 flex-shrink-0" />
              <span>Backlog</span>
              <span className="text-xs text-muted-foreground">(No Release)</span>
              {task.scope === 'backlog' && !task.release && (
                <Check className="ml-auto h-4 w-4" />
              )}
            </DropdownMenuItem>
            
            {backlogReleases.length > 0 && (
              <div className="pl-4 border-l-2 border-neutral-200 dark:border-neutral-800 ml-4 my-1 space-y-0.5 max-h-[150px] overflow-y-auto">
                {backlogReleases.map((release) => {
                  const colors = getReleaseStatusColor(release.status);
                  return (
                    <DropdownMenuItem
                      key={release.id}
                      onClick={() => handleReleaseChange(release)}
                      className="flex items-center gap-2 text-sm whitespace-nowrap pr-2"
                    >
                      <FiPackage className={cn("w-3.5 h-3.5 flex-shrink-0", colors.icon)} />
                      <span className="truncate">{release.name}</span>
                      {showReleaseDate && release.targetDate && (
                        <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                          {format(new Date(release.targetDate), 'MMM d')}
                        </span>
                      )}
                      {task.release?.id === release.id && (
                        <Check className="ml-auto h-4 w-4" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </div>
            )}
          </>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Move to Backlog Confirmation Dialog */}
      <Dialog open={showMoveToBacklogDialog} onOpenChange={setShowMoveToBacklogDialog}>
        <DialogContent className="max-w-lg" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-neutral-500" />
              Move to Backlog?
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-3">
              <p>
                You're assigning this task to <strong>"{pendingRelease?.name}"</strong>, which hasn't started yet.
              </p>
              <div className="bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800 rounded-lg p-3">
                <p className="text-sm text-violet-800 dark:text-violet-200">
                  Would you like to move this task out of the active week and into the backlog?
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button
              variant="outline"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                handleKeepInActiveWeek();
              }}
              loading={loadingAction === 'keep'}
              disabled={loadingAction !== null}
            >
              Keep in Active Week
            </Button>
            <Button 
              autoFocus
              onClick={(e) => {
                e.stopPropagation();
                handleMoveToBacklog();
              }}
              loading={loadingAction === 'move'}
              disabled={loadingAction !== null}
            >
              Move to Backlog
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}